/**
 * JARVIS neural voice: free, in-browser Kokoro speech (see
 * public/jarvis-voice-worker.js). Text is split into sentences and synthesized
 * one at a time, so the first sentence plays while the rest are generated.
 *
 * The model loads on first use. Until it is ready, voice.js falls back to the
 * browser's speechSynthesis, so JARVIS is never silent.
 */

const SETTINGS_KEY = 'jarvis.voice';
const ENGINE_KEY = 'jarvis.voiceEngine';

/** The BondHQ JARVIS voice — tuned in the Voice Lab (/jarvis/voice). */
export const DEFAULT_VOICE = Object.freeze({ mix: [['bm_george', 0.6], ['bm_fable', 0.4]], speed: 1.0 });

/** Stock voices worth blending for JARVIS (Kokoro British + American male). */
export const VOICE_OPTIONS = [
  { id: 'bm_george', label: 'George (British)' },
  { id: 'bm_fable', label: 'Fable (British)' },
  { id: 'bm_lewis', label: 'Lewis (British)' },
  { id: 'bm_daniel', label: 'Daniel (British)' },
  { id: 'am_michael', label: 'Michael (American)' },
  { id: 'am_onyx', label: 'Onyx (American)' },
  { id: 'am_adam', label: 'Adam (American)' },
];
const KNOWN = new Set(VOICE_OPTIONS.map((v) => v.id));

function sanitize(v) {
  const mix = (Array.isArray(v && v.mix) ? v.mix : [])
    .filter((m) => Array.isArray(m) && KNOWN.has(m[0]) && Number(m[1]) > 0)
    .map(([id, w]) => [id, Math.min(1, Number(w))]);
  const speed = Math.min(1.5, Math.max(0.7, Number(v && v.speed) || 1));
  return mix.length ? { mix, speed } : { mix: DEFAULT_VOICE.mix.map((m) => m.slice()), speed };
}

export function readVoiceSettings() {
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    return raw ? sanitize(JSON.parse(raw)) : sanitize(DEFAULT_VOICE);
  } catch (e) { return sanitize(DEFAULT_VOICE); }
}
export function writeVoiceSettings(v) {
  try { window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(sanitize(v))); } catch (e) { /* private mode */ }
}

export function neuralSupported() {
  return typeof window !== 'undefined' && typeof window.Worker === 'function'
    && typeof window.WebAssembly === 'object' && !!(window.AudioContext || window.webkitAudioContext);
}
/** 'neural' (default where supported) or 'browser'. */
export function readEngine() {
  try { if (window.localStorage.getItem(ENGINE_KEY) === 'browser') return 'browser'; } catch (e) { /* noop */ }
  return neuralSupported() ? 'neural' : 'browser';
}
export function writeEngine(engine) {
  try { window.localStorage.setItem(ENGINE_KEY, engine === 'browser' ? 'browser' : 'neural'); } catch (e) { /* noop */ }
  notify();
}

/** Split speakable text into sentence-sized chunks (Kokoro caps ~500 tokens). */
export function splitSentences(text, max = 220) {
  const parts = String(text || '').match(/[^.!?;:]+[.!?;:]+["')\]]*|[^.!?;:]+$/g) || [];
  const out = [];
  let cur = '';
  parts.map((p) => p.trim()).filter(Boolean).forEach((p) => {
    while (p.length > max) {
      const cut = p.lastIndexOf(',', max) > 40 ? p.lastIndexOf(',', max) + 1 : (p.lastIndexOf(' ', max) > 40 ? p.lastIndexOf(' ', max) : max);
      out.push(p.slice(0, cut).trim());
      p = p.slice(cut).trim();
    }
    // Glue very short fragments ("Done.") to the next sentence for natural prosody.
    if (cur && (cur + ' ' + p).length <= max && cur.length < 20) cur = `${cur} ${p}`;
    else { if (cur) out.push(cur); cur = p; }
  });
  if (cur) out.push(cur);
  return out.filter(Boolean);
}

// ---- engine state ---------------------------------------------------------

let worker = null;
// idle | loading | ready | slow (works, but can't keep up with playback) | failed
let status = 'idle';
let progress = 0;
let device = null;
let speed = null; // { rtf, threaded } measured by the worker at load
/** Below this many seconds of speech per second of compute, replies would stall. */
const MIN_RTF = 0.8;
let lastError = null;
const listeners = new Set();
function notify() { listeners.forEach((fn) => fn(getNeuralStatus())); }
export function getNeuralStatus() { return { status, progress, device, speed, error: lastError, engine: readEngine() }; }
export function subscribeNeural(fn) { listeners.add(fn); return () => listeners.delete(fn); }

let ctx = null;
let epoch = 1;
let nextId = 1;
let sources = [];
let pending = null; // { epoch, ids: Set, remaining, endAt, onEnd }

function audioCtx() {
  if (!ctx) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    ctx = new Ctor();
  }
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

function finish(job) {
  if (!job || pending !== job) return;
  pending = null;
  if (job.onEnd) job.onEnd();
}

function onAudio({ id, epoch: ep, audio, sampleRate }) {
  const job = pending;
  if (!job || ep !== job.epoch || !job.ids.has(id)) return;
  const c = audioCtx();
  const buf = c.createBuffer(1, audio.length, sampleRate);
  buf.copyToChannel(audio, 0);
  const src = c.createBufferSource();
  src.buffer = buf;
  src.connect(c.destination);
  const at = Math.max(c.currentTime + 0.02, job.endAt);
  src.start(at);
  job.endAt = at + buf.duration + 0.08; // short breath between sentences
  sources.push(src);
  job.remaining -= 1;
  if (job.remaining === 0) src.onended = () => finish(job);
}

function getWorker() {
  if (worker) return worker;
  const url = `${process.env.PUBLIC_URL || ''}/jarvis-voice-worker.js`;
  worker = new window.Worker(url, { type: 'module' });
  worker.onmessage = (e) => {
    const m = e.data || {};
    if (m.type === 'progress') { progress = m.pct; notify(); }
    else if (m.type === 'ready') {
      device = m.device;
      speed = { rtf: m.rtf, threaded: !!m.threaded };
      status = m.rtf && m.rtf < MIN_RTF ? 'slow' : 'ready';
      progress = 100;
      notify();
    }
    else if (m.type === 'audio') onAudio(m);
    else if (m.type === 'error') {
      lastError = m.message;
      if (status !== 'ready' && status !== 'slow') status = 'failed';
      notify();
      // A failed sentence ends the reply rather than leaving JARVIS "speaking".
      if (pending && (m.id == null || pending.ids.has(m.id))) { const job = pending; stopNeural(); if (job.onEnd) job.onEnd(); }
    }
  };
  worker.onerror = (e) => {
    status = 'failed';
    lastError = (e && e.message) || 'The voice engine could not start.';
    notify();
    if (pending) { const job = pending; stopNeural(); if (job.onEnd) job.onEnd(); }
  };
  return worker;
}

/** Start downloading/compiling the model in the background. */
export function preloadNeural() {
  if (!neuralSupported() || status === 'loading' || status === 'ready' || status === 'slow') return;
  status = 'loading';
  lastError = null;
  notify();
  try { getWorker().postMessage({ type: 'init' }); } catch (e) { status = 'failed'; lastError = e.message; notify(); }
}

/** Ready for live replies; 'slow' engines are only used in the Voice Lab. */
export const neuralReady = () => status === 'ready';
export const neuralLoaded = () => status === 'ready' || status === 'slow';

export function stopNeural() {
  epoch += 1;
  if (worker) worker.postMessage({ type: 'cancel', epoch });
  sources.forEach((s) => { try { s.onended = null; s.stop(); } catch (e) { /* not started */ } });
  sources = [];
  pending = null;
}

/** Speak plain text with the neural voice. Caller checks neuralReady()/neuralLoaded() first. */
export function speakNeural(text, onEnd, settings = readVoiceSettings()) {
  stopNeural();
  const chunks = splitSentences(text);
  if (!chunks.length) { if (onEnd) onEnd(); return; }
  const c = audioCtx();
  const job = { epoch, ids: new Set(), remaining: chunks.length, endAt: c.currentTime, onEnd };
  pending = job;
  const w = getWorker();
  chunks.forEach((chunk) => {
    const id = nextId++;
    job.ids.add(id);
    w.postMessage({ type: 'speak', id, epoch: job.epoch, text: chunk, mix: settings.mix, speed: settings.speed });
  });
}
